import * as R from 'ramda';
import { ApiReducer, makeApiAction, makeApiReducer, makeRsaaTypes } from '../../../../core/api/rsaa';
import { addError, addLoading, noLoadingNoError, getLoading } from '../../../../core/api/module';
import { Endpoints } from '../../../../core/api/endpoints';
import { AppState } from '../../../../core/redux/appState';
import { ActionType } from '../../../../core/redux/actionType';
import { DevPlanCollaborator } from '../models';


const KEY_ACTION = ActionType.AddDevPlanCollaboratorRequest;
const ACTIONS    = [
  ActionType.AddDevPlanCollaboratorRequest,
  ActionType.AddDevPlanCollaboratorResponse,
  ActionType.AddDevPlanCollaboratorError,
];


type Payload = DevPlanCollaborator;


type Meta = {
  email: string;
  projectId: number;
};


/**
 * Dispatch this to associate a new collaborator, by email address, with the dev plan for the specified project and
 * participant
 *
 * @param projectId - the database Id for the project the dev plan for which this person should be added
 * @param participantId - the database Id for the participant for whom this person should be added
 * @param email - the collaborator's email address
 * @param comment - additional text supplied by the user, to be included in the collaborator's invite email
 * @param languageId - the database Id for the language in which the email should be sent (usually just the project
 * default language)
 */
export const addDevPlanCollaboratorAction = (
  projectId: number,
  participantId: number,
  email: string,
  comment: string,
  languageId: number,
) => makeApiAction<Payload, Meta>(
  {
    method:   'POST',
    endpoint: Endpoints.AddDevPlanCollaborator,
    headers:  { 'content-type': 'application/json' },
    body:     JSON.stringify(
      {
        projectId:     projectId,
        participantId: participantId,
        to:            {
          toAddress:  email,
          languageId: languageId,
        },
        comment:       comment,
      },
    ),
    types:    makeRsaaTypes<Payload, Meta>(
      ACTIONS,
      {
        email:     email,
        projectId: projectId,
      },
    ),
    bailout:  (state: AppState) => !!getLoading(state, KEY_ACTION, `${email}-${projectId}`),
  },
);

const reducer: ApiReducer<Payload, Meta> = (state, action, errorMsg, isLoading, payload) => {
  const { email, projectId } = action.meta;

  const metaTag = `${email}-${projectId}`;

  if (isLoading) {
    return addLoading(state, KEY_ACTION, metaTag);
  }

  if (errorMsg) {
    return addError(state, KEY_ACTION, errorMsg, metaTag);
  }

  let successState = noLoadingNoError(state, KEY_ACTION, metaTag);

  if (!payload) {
    return successState;
  }

  const currentCollaborators = R.path<DevPlanCollaborator[]>(
    ['devPlan', 'collaborators', `${projectId}`],
    successState,
  );

  const withCollaboratorAdded = (currentCollaborators ?? []).concat(payload);

  successState = R.assocPath(
    ['devPlan', 'collaborators', `${projectId}`],
    withCollaboratorAdded,
    successState,
  );

  // Notify the user that the invite was successfully sent.
  return R.assocPath(
    ['devPlan', 'lastEmailRecipient'],
    payload.email,
    successState,
  );
};

export const addDevPlanCollaboratorReducer = makeApiReducer<Payload, Meta>(ACTIONS, KEY_ACTION, reducer);
